import { useState } from 'react';
import { InterviewPanel } from '@/features/interview/InterviewPanel';
import { initials } from './onboarding-beats-shared';

export interface OnboardingProfileCardPerson {
  name: string;
  role?: string;
  bio?: string;
}

export interface OnboardingProfileCardProps {
  name: string;
  email?: string;
  identityLine?: string;
  links?: { web?: string; linkedin?: string; x?: string };
  hasPublicPresence?: boolean;
  /** Date-classified upstream (classifyTrips) — "Upcoming" is genuinely future. */
  travelUpcoming?: Array<{ destination: string; dateRange: string }>;
  travelRecent?: Array<{ destination: string; dateRange: string }>;
  recurring?: string[];
  upcomingCount?: number;
  topics?: string[];
  /** Bio paragraphs (second-person portrait). Falls back to `portrait` if absent. */
  bio?: string[];
  /** @deprecated Use `bio` instead. */
  portrait?: string[];
  emailsRead?: number;
  eventsRead?: number;
  /** People section: bold name + em-dash role one-liner; expandable bio per person. */
  people?: OnboardingProfileCardPerson[];
  /** Faint provenance top bar: "Calendar 69 · Sent 17 · Inbox 498 — read" */
  provenance?: { calendar?: number; sent?: number; inbox?: number };
  /** While true: hide CTA row (pipeline still assembling). */
  assembling?: boolean;
  onConfirm: () => void;
  onFix: () => void;
}

export function OnboardingProfileCard({
  name,
  email,
  identityLine,
  links,
  hasPublicPresence,
  travelUpcoming,
  travelRecent,
  recurring,
  upcomingCount,
  topics,
  bio,
  portrait,
  emailsRead,
  eventsRead,
  people,
  provenance,
  assembling,
  onConfirm,
  onFix,
}: OnboardingProfileCardProps) {
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [peopleExpanded, setPeopleExpanded] = useState(false);

  const avatarInitials = initials(name, email ?? '');

  // bio takes precedence over the deprecated portrait prop
  const displayBio = bio && bio.length > 0 ? bio : portrait;

  const hasLinks = !!(links?.web || links?.linkedin || links?.x);
  const showFootprint = hasLinks || hasPublicPresence;
  const showTravel = (travelUpcoming?.length ?? 0) + (travelRecent?.length ?? 0) > 0;
  const showMeetings = (recurring && recurring.length > 0) || (upcomingCount && upcomingCount > 0);
  const showTopics = topics && topics.length > 0;
  const showPeople = people && people.length > 0;

  const footerParts: string[] = [];
  if (emailsRead) footerParts.push(`Read ${emailsRead} emails`);
  if (eventsRead) footerParts.push(`${eventsRead} calendar events`);
  const footerCounts = footerParts.join(' · ');

  // Provenance bar parts
  const provParts: string[] = [];
  if (provenance?.calendar) provParts.push(`Calendar ${provenance.calendar}`);
  if (provenance?.sent) provParts.push(`Sent ${provenance.sent}`);
  if (provenance?.inbox) provParts.push(`Inbox ${provenance.inbox}`);
  const showProvenance = provParts.length > 0;

  const handleConfirm = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(onConfirm, 240);
  };

  const handleFix = () => {
    setInterviewOpen(true);
  };

  void onFix;

  return (
    <>
      <div className="opc-card obr-fade">
        {/* Provenance top bar */}
        {showProvenance && (
          <div className="opc-provenance">
            {provParts.join(' · ')}
            {' — read'}
          </div>
        )}

        {/* Header row */}
        <div className="opc-header">
          <div className="opc-avatar">{avatarInitials}</div>
          <div className="opc-identity">
            <div className="opc-name">{name}</div>
            {identityLine && <div className="opc-identity-line">{identityLine}</div>}
          </div>
          {(hasLinks || email) && (
            <div className="opc-links">
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="opc-link"
                  target="_blank"
                  rel="noreferrer"
                  title="Email"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  <span>email</span>
                </a>
              )}
              {links?.linkedin && (
                <a
                  href={links.linkedin}
                  className="opc-link"
                  target="_blank"
                  rel="noreferrer"
                  title="LinkedIn"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                  <span>LinkedIn</span>
                </a>
              )}
              {links?.x && (
                <a
                  href={links.x}
                  className="opc-link"
                  target="_blank"
                  rel="noreferrer"
                  title="X / Twitter"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.741l7.73-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635Zm-1.161 17.52h1.833L7.084 4.126H5.117Z"/></svg>
                  <span>X</span>
                </a>
              )}
              {links?.web && (
                <a
                  href={links.web}
                  className="opc-link"
                  target="_blank"
                  rel="noreferrer"
                  title="Website"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  <span>web</span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Bio / Portrait excerpt (1-2 paragraphs, muted) */}
        {displayBio && displayBio.length > 0 && (
          <div className="opc-portrait opc-fade">
            {displayBio.slice(0, 2).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        <div className="opc-sections">
          {/* PEOPLE */}
          {showPeople && (
            <div className="opc-section opc-fade">
              <div className="opc-section-label">People</div>
              <div className="opc-people-rows">
                {people!.map((p) => (
                  <div className="opc-person-row" key={p.name}>
                    <div className="opc-person-oneliner">
                      <strong>{p.name}</strong>
                      {p.role && <span className="opc-person-role"> — {p.role}</span>}
                    </div>
                    {peopleExpanded && p.bio && (
                      <p className="opc-person-bio opc-fade">{p.bio}</p>
                    )}
                  </div>
                ))}
              </div>
              {people!.some((p) => p.bio) && (
                <button
                  type="button"
                  className="opc-people-toggle"
                  onClick={() => setPeopleExpanded((v) => !v)}
                >
                  {peopleExpanded
                    ? 'hide details'
                    : 'one-line summaries hidden ›'}
                </button>
              )}
            </div>
          )}

          {/* TRAVEL — trips are date-classified upstream (classifyTrips);
              "Upcoming" only ever shows genuinely future trips. */}
          {showTravel && (
            <div className="opc-section opc-fade">
              <div className="opc-section-label">Travel</div>
              <div className="opc-section-body">
                {(travelUpcoming ?? []).slice(0, 2).map((t, i) => (
                  <div className="opc-travel-row" key={`u${i}`}>
                    <span className="opc-travel-upcoming">Upcoming</span>
                    <span className="opc-travel-dest">{t.destination}</span>
                    <span className="opc-travel-date">{t.dateRange}</span>
                  </div>
                ))}
                {(travelRecent ?? []).length > 0 && (
                  <div className="opc-travel-recent">
                    <span className="opc-travel-recent-label">Recent</span>
                    {travelRecent!.slice(0, 4).map((t, i) => (
                      <span key={i} className="opc-travel-dest">{t.destination}{i < Math.min(travelRecent!.length, 4) - 1 ? ' ·' : ''}</span>
                    ))}
                  </div>
                )}
                <span className="opc-caption">from your calendar</span>
              </div>
            </div>
          )}

          {/* MEETINGS */}
          {showMeetings && (
            <div className="opc-section opc-fade">
              <div className="opc-section-label">Meetings</div>
              <div className="opc-section-body">
                <span className="opc-meetings-text">
                  {upcomingCount ? `${upcomingCount} coming up. ` : ''}
                  {recurring && recurring.length > 0
                    ? `Recurring 1:1s — ${recurring.slice(0, 3).join(' · ')}.`
                    : ''}
                </span>
                <span className="opc-caption">from your calendar</span>
              </div>
            </div>
          )}

          {/* WHAT YOU'RE DISCUSSING */}
          {showTopics && (
            <div className="opc-section opc-fade">
              <div className="opc-section-label">Discussing</div>
              <div className="opc-section-body">
                <span>{topics!.join(' · ')}</span>
                <span className="opc-caption">from your mail</span>
              </div>
            </div>
          )}

          {/* PUBLIC FOOTPRINT — now at bottom as link chips */}
          {showFootprint && (
            <div className="opc-section opc-fade">
              <div className="opc-section-label">Footprint</div>
              <div className="opc-section-body opc-footprint-body">
                <div className="opc-footprint-chips">
                  {links?.linkedin && (
                    <a
                      href={links.linkedin}
                      className="opc-fp-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn
                    </a>
                  )}
                  {links?.x && (
                    <a
                      href={links.x}
                      className="opc-fp-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      X
                    </a>
                  )}
                  {links?.web && (
                    <a
                      href={links.web}
                      className="opc-fp-chip"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Web
                    </a>
                  )}
                </div>
                {hasPublicPresence && (
                  <span className="opc-presence-badge">Established public presence</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="opc-footer">
          {footerCounts && <span>{footerCounts}. </span>}
          <span>Nothing left your account.</span>
        </div>

        {/* CTA row — hidden while assembling */}
        {!assembling && (
          <div className="opc-cta opc-fade">
            <button
              type="button"
              className={`opc-btn-confirm${leaving ? ' is-leaving' : ''}`}
              disabled={leaving}
              onClick={handleConfirm}
            >
              Looks right &mdash; let&apos;s go
            </button>
            <button
              type="button"
              className="opc-btn-fix"
              onClick={handleFix}
            >
              Fix something
            </button>
            <span className="opc-cta-hint">next: your chat, commands ready</span>
          </div>
        )}
      </div>

      {interviewOpen && (
        <InterviewPanel
          onClose={() => setInterviewOpen(false)}
          seedQuestion="What did I get wrong? Tell me and I'll fix it — names, roles, anything."
        />
      )}
    </>
  );
}
